import React, { useState } from 'react';
import { ShareOptionsPopup } from './ShareOptionsPopup';
import { DownloadOptionsPopup } from './DownloadOptionsPopup';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import AboutDataCard from '@/components/shared/AboutTheData';

interface MetricDefinition {
  aboutDescription: string;
  sources: {
    sourceName: string;
    datasetName: string;
    datasetLink: string;
    variables: {
      name: string;
      commonName: string;
      url: string;
    }[];
    retrievedOn: string;
  }[];
  metricDefinition: string;
  calculationFormula: string;
}

interface ChartHeaderButtonsProps {
  chartName: string;
  shareLink: string;
  isDownloading?: boolean;
  isDownloadPopupOpen?: boolean;
  onDownloadClick?: () => void;
  onDownloadOption?: (option: 'single' | 'all') => Promise<void>;
  onDownloadPopupClose?: () => void;
  metricDefinition?: MetricDefinition;
  aboutDescription?: string;
}

export const snapshot_ChartHeaderButtons: React.FC<ChartHeaderButtonsProps> = ({ 
  chartName, 
  shareLink,
  isDownloading = false,
  isDownloadPopupOpen = false,
  onDownloadClick,
  onDownloadOption,
  onDownloadPopupClose,
  metricDefinition,
  aboutDescription
}) => {
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [isAboutDataOpen, setIsAboutDataOpen] = useState(false);

  const handleShareClick = () => {
    setIsSharePopupOpen(true);
  };

  const handleDownloadClick = () => {
    if (isDownloading) return; // Prevent clicks during download
    onDownloadClick?.();
  };

  const handleAboutDataClick = () => {
    setIsAboutDataOpen(true);
  };

  return (
    <>
      <div className="absolute top-6 right-6 z-10 flex items-center gap-4 chart-action-buttons">
        <button 
          aria-label="Download chart" 
          onClick={handleDownloadClick}
          disabled={isDownloading}
          className="group relative"
        >
          {isDownloading ? (
            <LoadingSpinner size="sm" className="text-gray-600" />
          ) : (
            <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 group-hover:opacity-100 transition-opacity">
              download
            </span>
          )}
        </button>
        <button aria-label="Share chart" onClick={handleShareClick} className="group">
          <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 group-hover:opacity-100 transition-opacity">
            share
          </span>
        </button>
        <button aria-label="About the Data" className="group" onClick={handleAboutDataClick}>
          <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 group-hover:opacity-100 transition-opacity">
            info
          </span>
        </button>
      </div>
      
      {/* Download options modal */}
      <DownloadOptionsPopup
        isOpen={isDownloadPopupOpen}
        chartName={chartName}
        onDownload={onDownloadOption || (async () => {})}
        onClose={onDownloadPopupClose || (() => {})}
      />
      
      {/* Share options modal */}
      <ShareOptionsPopup
        isOpen={isSharePopupOpen}
        chartName={chartName}
        shareLink={shareLink}
        onClose={() => setIsSharePopupOpen(false)}
      />

      {isAboutDataOpen && metricDefinition && (
        <AboutDataCard 
          onClose={() => setIsAboutDataOpen(false)}
          aboutDescription={aboutDescription || metricDefinition.aboutDescription}
          sources={metricDefinition.sources}
          metricDefinition={metricDefinition.metricDefinition}
          calculationFormula={metricDefinition.calculationFormula}
        />
      )}
    </>
  );
};